/**
 * Query hook for the global search modal (patch D4).
 *
 * Returns one bucket per searchable entity kind, each capped at the
 * server's PER_BUCKET_LIMIT. The modal renders each bucket as a section
 * and groups results with section headers.
 *
 * Caller is expected to debounce — the hook itself uses
 * `keepPreviousData` so the UI doesn't flash empty between keystrokes.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiRequest } from './api-client';

export interface ProjectSearchResult {
  kind: 'project';
  id: string;
  projectNumber: string;
  projectTitle: string;
  lifecycleState: 'OPEN' | 'CLOSED';
}

export interface ItemSearchResult {
  kind: 'item';
  id: string;
  name: string;
  itemType: 'PART' | 'SUB_PART';
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  parentItemName: string | null;
}

export interface SupplierSearchResult {
  kind: 'supplier';
  id: string;
  name: string;
  isActive: boolean;
}

// Patch 3 — tag + communication results
export interface TagSearchResult {
  kind: 'tag';
  name: string;
  usageCount: number;
}

export interface CommunicationSearchResult {
  kind: 'communication';
  id: string;
  subject: string;
  threadId: string;
  occurredAt: string | null;
  projectId: string | null;
  projectNumber: string | null;
}

export interface SearchResponse {
  query: string;
  projects: ProjectSearchResult[];
  items: ItemSearchResult[];
  suppliers: SupplierSearchResult[];
  tags: TagSearchResult[];
  communications: CommunicationSearchResult[];
}

export type AnySearchResult =
  | ProjectSearchResult
  | ItemSearchResult
  | SupplierSearchResult
  | TagSearchResult
  | CommunicationSearchResult;

export function useGlobalSearch(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['search', trimmed],
    queryFn: ({ signal }) =>
      apiRequest<SearchResponse>(`/api/search?q=${encodeURIComponent(trimmed)}`, { signal }),
    enabled: trimmed.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Flatten the bucketed response into a single linear list in display
 * order. Used by the modal's keyboard-arrow navigation (the "currently
 * highlighted result" needs a single index, not a `(bucket, position)`
 * tuple).
 *
 * Order: projects → items → suppliers → tags → communications. Matches the
 * visual order in the modal and keeps results from the same bucket together.
 */
export function flattenSearchResults(data: SearchResponse | undefined): AnySearchResult[] {
  if (!data) return [];
  return [
    ...data.projects,
    ...data.items,
    ...data.suppliers,
    ...data.tags,
    ...data.communications,
  ];
}

/**
 * Compute the navigation URL for a result, used both by click handlers
 * and Enter-key activation. Suppliers go to the suppliers list anchored
 * at the supplier id (the page deep-links by hash); projects and items
 * have direct routes.
 */
export function searchResultHref(r: AnySearchResult): string {
  switch (r.kind) {
    case 'project':
      return `/projects/${r.id}`;
    case 'item':
      return `/projects/${r.projectId}/items/${r.id}`;
    case 'supplier':
      // No standalone supplier page; the Settings -> Suppliers list is the
      // canonical landing place. Linking with hash lets future work scroll
      // to the row.
      return `/settings#supplier-${r.id}`;
    case 'tag':
      // Tag search drills into the cross-project communications page filtered
      // by that tag.
      return `/communications?tag=${encodeURIComponent(r.name)}`;
    case 'communication':
      // Communication results open the relevant attachment in the Documents tab
      // of its project (or the cross-project page if master-anchored).
      return r.projectId
        ? `/projects/${r.projectId}?tab=documents&open=${r.id}`
        : `/communications?open=${r.id}`;
  }
}
